import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiErrorDetail } from "../lib/api";
import { Plus, Pencil, Trash2, Building2, Search, X, Eye } from "lucide-react";
import { toast } from "sonner";
import ToconlineImport from "../components/ToconlineImport";
import ExcelImport from "../components/ExcelImport";
import ExportExcelButton from "../components/ExportExcelButton";
import EmpresaSearchSelect from "../components/EmpresaSearchSelect";

const EMPTY = {
  nome: "",
  nif: "",
  morada: "",
  codigo_postal: "",
  localidade: "",
  administrador_nome: "",
  administrador_telefone: "",
  administrador_email: "",
  notas: "",
};

export default function Condominios() {
  const [items, setItems] = useState([]);
  const [empresas, setEmpresas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [openForm, setOpenForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  async function refresh() {
    setLoading(true);
    try {
      const [c, e] = await Promise.all([
        api.get("/condominios"),
        api.get("/empresas-condominio"),
      ]);
      setItems(c.data);
      setEmpresas(e.data);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    refresh();
  }, []);

  function openCreate() {
    setEditing(null);
    setForm(EMPTY);
    setOpenForm(true);
  }

  function openEdit(item) {
    setEditing(item);
    setForm({ ...EMPTY, ...item });
    setOpenForm(true);
  }

  async function onSubmit(e) {
    e.preventDefault();
    setSaving(true);
    try {
      if (editing) {
        await api.put(`/condominios/${editing.id}`, form);
        toast.success("Condomínio atualizado");
      } else {
        await api.post("/condominios", form);
        toast.success("Condomínio criado");
      }
      setOpenForm(false);
      refresh();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setSaving(false);
    }
  }

  async function onDelete(item) {
    if (!window.confirm(`Eliminar condomínio "${item.nome}"? Esta ação apaga também serviços e ocorrências associados.`))
      return;
    try {
      await api.delete(`/condominios/${item.id}`);
      toast.success("Condomínio eliminado");
      refresh();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  }

  const filtered = items.filter((i) => {
    const q = search.toLowerCase();
    return (
      !q ||
      i.nome.toLowerCase().includes(q) ||
      i.nif.toLowerCase().includes(q) ||
      i.morada.toLowerCase().includes(q) ||
      (i.empresa_condominio_nome || "").toLowerCase().includes(q)
    );
  });

  return (
    <div className="space-y-6" data-testid="condominios-page">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div className="relative flex-1 max-w-md">
          <Search className="w-4 h-4 text-neutral-400 absolute left-3 top-1/2 -translate-y-1/2" strokeWidth={1.5} />
          <input
            data-testid="condominios-search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Pesquisar por nome, NIF, morada ou administrador…"
            className="w-full pl-10 pr-4 py-2.5 bg-white border border-zinc-200 rounded-sm text-sm font-manrope focus:ring-2 focus:ring-[#0047FF]/20 focus:border-[#0047FF] outline-none"
          />
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <ExportExcelButton
            endpoint="/condominios/export/xlsx"
            filename="condominios.xlsx"
            testid="export-condominios-btn"
          />
          <ExcelImport onImported={refresh} />
          <ToconlineImport onImported={refresh} />
          <button
            data-testid="condominios-new-btn"
            onClick={openCreate}
            className="bg-[#0047FF] text-white hover:bg-[#003BCC] rounded-sm px-5 py-2.5 font-manrope font-semibold text-sm transition-colors flex items-center gap-2"
          >
            <Plus className="w-4 h-4" strokeWidth={2} />
            Novo condomínio
          </button>
        </div>
      </div>

      <div className="bg-white border border-zinc-200 rounded-sm overflow-hidden">
        {loading ? (
          <div className="p-10 text-center text-sm font-manrope text-neutral-500">A carregar…</div>
        ) : filtered.length === 0 ? (
          <div className="p-14 text-center">
            <Building2 className="w-8 h-8 text-neutral-300 mx-auto mb-4" strokeWidth={1.5} />
            <div className="text-sm font-manrope text-neutral-500">
              {items.length === 0 ? "Ainda não tem condomínios registados." : "Sem resultados."}
            </div>
          </div>
        ) : (
          <table className="w-full">
            <thead>
              <tr className="bg-zinc-50/50 border-b border-zinc-200 text-[10px] uppercase tracking-[0.2em] text-neutral-500 font-chivo font-bold">
                <th className="py-3 px-6 text-left">Nome</th>
                <th className="py-3 px-6 text-left">NIF</th>
                <th className="py-3 px-6 text-left">Morada</th>
                <th className="py-3 px-6 text-left">Empresa de Condomínio</th>
                <th className="py-3 px-6 text-left">Registado por</th>
                <th className="py-3 px-6 text-right">Ações</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((c) => (
                <tr
                  key={c.id}
                  className="border-b border-zinc-100 hover:bg-zinc-50/60 transition-colors"
                  data-testid={`condominio-row-${c.id}`}
                >
                  <td className="py-4 px-6 font-manrope text-sm text-neutral-950 font-semibold">
                    <Link
                      data-testid={`open-${c.id}`}
                      to={`/condominios/${c.id}`}
                      className="hover:text-[#0047FF] transition-colors"
                    >
                      {c.nome}
                    </Link>
                  </td>
                  <td className="py-4 px-6 font-manrope text-sm text-neutral-600 font-mono">
                    {c.nif}
                  </td>
                  <td className="py-4 px-6 font-manrope text-sm text-neutral-600">
                    {c.morada}
                    {c.localidade ? `, ${c.localidade}` : ""}
                  </td>
                  <td className="py-4 px-6 font-manrope text-sm text-neutral-700">
                    {c.empresa_condominio_nome || <span className="text-neutral-400">—</span>}
                  </td>
                  <td className="py-4 px-6 font-manrope text-sm text-neutral-700">
                    {c.created_by_name || c.created_by_email ? (
                      <div>
                        <div>{c.created_by_name || "—"}</div>
                        {c.created_by_email && (
                          <div className="text-xs text-neutral-500 font-mono">{c.created_by_email}</div>
                        )}
                      </div>
                    ) : (
                      <span className="text-neutral-400">—</span>
                    )}
                  </td>
                  <td className="py-4 px-6 text-right">
                    <div className="inline-flex items-center gap-1">
                      <Link
                        data-testid={`view-${c.id}`}
                        to={`/condominios/${c.id}`}
                        className="p-2 text-neutral-500 hover:text-[#0047FF] hover:bg-[#0047FF]/5 rounded-sm transition-colors"
                        title="Ver detalhe"
                      >
                        <Eye className="w-4 h-4" strokeWidth={1.5} />
                      </Link>
                      <button
                        data-testid={`edit-${c.id}`}
                        onClick={() => openEdit(c)}
                        className="p-2 text-neutral-500 hover:text-[#0047FF] hover:bg-[#0047FF]/5 rounded-sm transition-colors"
                        title="Editar"
                      >
                        <Pencil className="w-4 h-4" strokeWidth={1.5} />
                      </button>
                      <button
                        data-testid={`delete-${c.id}`}
                        onClick={() => onDelete(c)}
                        className="p-2 text-neutral-500 hover:text-[#FF4500] hover:bg-[#FF4500]/5 rounded-sm transition-colors"
                        title="Eliminar"
                      >
                        <Trash2 className="w-4 h-4" strokeWidth={1.5} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {openForm && (
        <div
          className="fixed inset-0 bg-neutral-950/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          onClick={() => setOpenForm(false)}
          data-testid="condominios-modal"
        >
          <div
            className="bg-white rounded-sm w-full max-w-2xl max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="sticky top-0 bg-white border-b border-zinc-200 px-8 py-5 flex items-center justify-between">
              <div>
                <div className="text-[10px] uppercase tracking-[0.24em] text-neutral-400 font-chivo font-bold mb-1">
                  {editing ? "Editar registo" : "Novo registo"}
                </div>
                <h2 className="text-xl font-chivo font-black text-neutral-950 tracking-tight">
                  {editing ? "Editar condomínio" : "Novo condomínio"}
                </h2>
              </div>
              <button
                data-testid="condominios-close-modal"
                onClick={() => setOpenForm(false)}
                className="p-2 text-neutral-400 hover:text-neutral-900 rounded-sm hover:bg-zinc-100 transition-colors"
              >
                <X className="w-5 h-5" strokeWidth={1.5} />
              </button>
            </div>
            <form onSubmit={onSubmit} className="p-8 space-y-5" data-testid="condominios-form">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <Field label="Nome do edifício *" required value={form.nome} onChange={(v) => setForm({ ...form, nome: v })} testid="input-nome" />
                <Field label="NIF *" required value={form.nif} onChange={(v) => setForm({ ...form, nif: v })} testid="input-nif" />
              </div>
              <Field label="Morada *" required value={form.morada} onChange={(v) => setForm({ ...form, morada: v })} testid="input-morada" />
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <Field label="Código postal" value={form.codigo_postal} onChange={(v) => setForm({ ...form, codigo_postal: v })} testid="input-cp" placeholder="1000-100" />
                <Field label="Localidade" value={form.localidade} onChange={(v) => setForm({ ...form, localidade: v })} testid="input-localidade" />
              </div>
              <div className="border-t border-zinc-200 pt-5">
                <EmpresaSearchSelect
                  value={form.empresa_condominio_id}
                  onChange={(v) => setForm({ ...form, empresa_condominio_id: v })}
                  empresas={empresas}
                  testid="input-empresa"
                  required
                />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-[0.2em] text-neutral-500 font-chivo font-bold block mb-2">
                  Notas
                </label>
                <textarea
                  data-testid="input-notas"
                  value={form.notas}
                  onChange={(e) => setForm({ ...form, notas: e.target.value })}
                  rows={3}
                  className="w-full bg-white border border-zinc-200 rounded-sm px-4 py-2.5 font-manrope text-sm text-neutral-900 focus:ring-2 focus:ring-[#0047FF]/20 focus:border-[#0047FF] transition-all outline-none resize-none"
                />
              </div>
              <div className="flex items-center justify-end gap-3 pt-4 border-t border-zinc-200">
                <button
                  type="button"
                  data-testid="cancel-btn"
                  onClick={() => setOpenForm(false)}
                  className="bg-white text-neutral-900 border border-zinc-200 hover:bg-zinc-50 rounded-sm px-5 py-2.5 font-manrope font-medium text-sm transition-colors"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  data-testid="save-btn"
                  disabled={saving}
                  className="bg-[#0047FF] text-white hover:bg-[#003BCC] rounded-sm px-6 py-2.5 font-manrope font-semibold text-sm transition-colors disabled:opacity-60"
                >
                  {saving ? "A guardar…" : editing ? "Guardar alterações" : "Criar condomínio"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

function Field({ label, value, onChange, required, type = "text", testid, placeholder }) {
  return (
    <div>
      <label className="text-[10px] uppercase tracking-[0.2em] text-neutral-500 font-chivo font-bold block mb-2">
        {label}
      </label>
      <input
        data-testid={testid}
        type={type}
        required={required}
        value={value || ""}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        className="w-full bg-white border border-zinc-200 rounded-sm px-4 py-2.5 font-manrope text-sm text-neutral-900 focus:ring-2 focus:ring-[#0047FF]/20 focus:border-[#0047FF] transition-all outline-none"
      />
    </div>
  );
}
